<template>
  <component
    :is="tag"
    v-bind="bindings"
    type="button"
    class="bui-tbl-icon-btn"
    :class="{ disabled }"
    :title="title"
    :aria-label="ariaLabel || title"
    :aria-disabled="disabled || undefined"
    :data-testid="dataTestId"
    @click="onClick"
  >
    <span class="ring" aria-hidden="true" />
    <span class="ico">
      <slot />
    </span>
  </component>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

const props = withDefaults(
  defineProps<{
    to?: string;
    href?: string;
    title?: string;
    ariaLabel?: string;
    disabled?: boolean;
    dataTestId?: string;
  }>(),
  { disabled: false },
);

const emit = defineEmits<{ click: [event: MouseEvent] }>();

const tag = computed(() => {
  if (props.to) return RouterLink;
  if (props.href) return 'a';
  return 'button';
});

const bindings = computed<Record<string, unknown>>(() => {
  if (props.to) return { to: props.to };
  if (props.href) return { href: props.href };
  return {};
});

function onClick(e: MouseEvent) {
  if (props.disabled) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }
  emit('click', e);
}
</script>

<style lang="scss" scoped>
.bui-tbl-icon-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--bui-tbl-rowbtn-size, 28px);
  height: var(--bui-tbl-rowbtn-size, 28px);
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: var(--bui-tbl-rowbtn-radius, 32px);
  color: var(--bui-tbl-rowbtn-color, #2a2969);
  cursor: pointer;
  text-decoration: none;
  flex-shrink: 0;

  .ring {
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: var(--bui-tbl-rowbtn-hover-fill, #edeeff);
    opacity: 0;
    transition: opacity 120ms ease;
  }

  .ico {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    line-height: 0;

    :deep(svg) {
      width: 16px;
      height: 16px;
      display: block;
    }
  }

  &:hover:not(.disabled) .ring {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid var(--bui-chk-checked-fill, #de3da9);
    outline-offset: 2px;
  }

  &.disabled {
    cursor: default;
    opacity: 0.35;
  }
}
</style>
